<template>
  <div
    ref="scroller"
    class="ai-thread"
    @scroll.passive="onScroll"
    @click="onClick"
  >
    <div
      v-if="!thread"
      class="ai-welcome"
    >
      <div class="ai-welcome__badge">
        <ai-icon
          name="assistant"
          :size="22"
        />
      </div>
      <h3 class="ai-welcome__title">
        Чем помочь?
      </h3>
      <p class="ai-welcome__text">
        Спросите о работе сотрудников, программах и сайтах, переписке, файлах и устройствах. Ответ строится по данным
        мониторинга, у каждого факта — ссылка на запись.
      </p>
      <div class="ai-welcome__examples">
        <button
          v-for="example in examples"
          :key="example"
          type="button"
          class="ai-chip-button"
          :disabled="disabled"
          @click="$emit('example', example)"
        >
          {{ example }}
        </button>
      </div>
    </div>

    <template v-else>
      <div
        v-if="thread.loading && !thread.messages.length"
        class="ai-thread__loading"
      >
        <span class="ai-spinner" />
        Загружаю…
      </div>

      <section
        v-if="thread.conversation && (scope || thread.messages.length)"
        class="ai-scope"
        :class="{ 'is-chat': !scope }"
      >
        <div class="ai-scope__head">
          <div class="ai-scope__title">
            <ai-icon
              :name="scope ? 'search' : 'chat'"
              :size="15"
            />
            <span>{{ thread.conversation.title || 'Без названия' }}</span>
          </div>
          <button
            type="button"
            class="ai-button is-small is-ghost ai-scope__export"
            :disabled="!canExport"
            :title="exportTitle"
            @click="exportPdf"
          >
            <span
              v-if="exporting"
              class="ai-spinner ai-spinner--small"
            />
            <ai-icon
              v-else
              name="download"
              :size="14"
            />
            {{ exporting ? 'Готовлю PDF…' : 'Экспорт в PDF' }}
          </button>
        </div>
        <div
          v-if="scope"
          class="ai-scope__facts"
        >
          <span>{{ scope.period }}</span>
          <span>{{ scopeComputers }}</span>
          <span :title="scope.sources">{{ scopeSources }}</span>
        </div>
        <p
          v-if="exportError"
          class="ai-scope__error"
          role="alert"
        >
          {{ exportError }}
        </p>
      </section>

      <template v-for="(message, index) in thread.messages">
        <!-- Every message has one .ai-message element: citations find their message by its position. -->
        <div
          v-if="message.role === 'user'"
          :key="message.id"
          class="ai-message is-user"
        >
          <div
            v-if="message.content"
            class="ai-bubble"
            v-html="plain(message.content)"
          />
          <ul
            v-if="filesOf(message).length"
            class="ai-message__files"
            aria-label="Приложенные файлы"
          >
            <li
              v-for="(file, fileIndex) in filesOf(message)"
              :key="file.id || fileIndex"
            >
              <attachment-chip
                :name="file.name || 'Файл'"
                :size="file.size"
                :type="file.type || ''"
                :openable="!!file.evidence"
                @open="openAttachment(message, file)"
              />
            </li>
          </ul>
        </div>

        <div
          v-else
          :key="message.id"
          class="ai-message is-assistant"
        >
          <steps-timeline
            :message-id="message.id"
            :steps="stepsOf(message)"
            :live="isLive(message)"
            :status="isLive(message) ? thread.live.status : null"
            :progress="isLive(message) ? thread.live.progress : null"
            :duration-ms="stats(message).durationMs || null"
          />

          <div
            v-if="bodyOf(message)"
            class="ai-markdown"
            :class="{ 'is-streaming': isLive(message) }"
            v-html="render(message)"
          />
          <div
            v-else-if="isLive(message)"
            class="ai-typing"
            aria-label="Ассистент готовит ответ"
          >
            <span />
            <span />
            <span />
          </div>

          <ul
            v-if="notesOf(message).length"
            class="ai-notes"
          >
            <li
              v-for="note in notesOf(message)"
              :key="note"
            >
              <ai-icon
                name="info"
                :size="14"
              />
              <span>{{ note }}</span>
            </li>
          </ul>

          <div
            v-if="problem(message)"
            class="ai-problem ai-tone"
            :class="`is-${problem(message).tone}`"
          >
            <ai-icon
              :name="problem(message).tone === 'danger' ? 'alert' : 'info'"
              :size="15"
            />
            <span>{{ problem(message).text }}</span>
            <button
              v-if="thread.canResume && index === thread.messages.length - 1"
              type="button"
              class="ai-button is-small"
              :disabled="busy"
              @click="$emit('resume')"
            >
              <ai-icon
                name="resume"
                :size="14"
              />
              Продолжить
            </button>
          </div>

          <div
            v-if="!isLive(message) && (message.content || refsOf(message).length)"
            class="ai-message__footer"
          >
            <button
              v-if="refsOf(message).length || evidenceOf(message).length"
              type="button"
              class="ai-button is-small is-ghost"
              @click="$emit('evidence', { messageId: message.id, ids: refsOf(message) })"
            >
              <ai-icon
                name="evidence"
                :size="14"
              />
              {{ evidenceLabel(message) }}
            </button>
            <button
              v-if="message.content"
              type="button"
              class="ai-button is-small is-ghost"
              :title="copied === message.id ? 'Скопировано' : 'Копировать ответ'"
              @click="copy(message)"
            >
              <ai-icon
                :name="copied === message.id ? 'check' : 'copy'"
                :size="14"
              />
              {{ copied === message.id ? 'Скопировано' : 'Копировать' }}
            </button>
            <span
              v-if="statsText(message)"
              class="ai-message__stats"
              :title="statsHint(message)"
            >
              {{ statsText(message) }}
            </span>
          </div>

          <memory-suggestions
            v-if="!isLive(message) && meta(message).memory"
            :memory="meta(message).memory"
          />
        </div>
      </template>

      <p
        v-if="thread.error"
        class="ai-thread__error"
      >
        {{ thread.error }}
      </p>
    </template>
  </div>
</template>

<script>
  import { getEvidence } from '@/api/methods/assistant';
  import AiIcon from './AiIcon.vue';
  import AttachmentChip from './AttachmentChip.vue';
  import MemorySuggestions from './MemorySuggestions.vue';
  import StepsTimeline from './StepsTimeline.vue';
  import { citedIds, renderAnswer, renderPlain } from './markdown';
  import { count, duration, tokens } from './format';

  const EXAMPLES = [
    'Кто вчера работал дольше всех?',
    'Какие программы чаще всего использовали на этой неделе?',
    'Найди упоминания слова «договор» в переписке за месяц',
    'Подключали ли внешние накопители за последние 7 дней?',
  ];
  const PROBLEMS = {
    failed: { tone: 'danger', text: 'Ответ не получен из-за ошибки.' },
    interrupted: { tone: 'warning', text: 'Работа была прервана (служба перезапускалась). Можно продолжить.' },
    cancelled: { tone: 'warning', text: 'Остановлено пользователем.' },
  };

  export default {
    name: 'ChatThread',
    components: { AiIcon, AttachmentChip, MemorySuggestions, StepsTimeline },
    props: {
      thread: { type: Object, default: null },
      disabled: { type: Boolean, default: false },
      busy: { type: Boolean, default: false },
    },
    data: () => ({ stick: true, copied: null, examples: EXAMPLES, exporting: false, exportError: '' }),
    computed: {
      // «Экспорт в PDF»: when there is an answer and no turn is running.
      canExport() {
        const { conversation, live, messages } = this.thread;
        const running = !!live || conversation.status === 'running';
        return !this.exporting && !this.busy && !running && messages.some((message) => message.role === 'assistant');
      },
      exportTitle() {
        if (this.exporting) return 'Готовлю PDF…';
        if (!this.canExport) return 'Экспорт станет доступен, когда ассистент закончит ответ';
        return 'Сохранить вопросы, ответы и доказательства в PDF';
      },
      scope() {
        const conversation = this.thread && this.thread.conversation;
        return conversation && conversation.mode === 'investigation' ? conversation.scopeInfo : null;
      },
      scopeComputers() {
        return count(this.scope.computers || 0, ['компьютер', 'компьютера', 'компьютеров']);
      },
      scopeSources() {
        const sources = String(this.scope.sources || '')
          .split(', ')
          .filter(Boolean);
        if (sources.length <= 3) return sources.join(', ');
        return `${sources.slice(0, 3).join(', ')} и ещё ${sources.length - 3}`;
      },
      signature() {
        if (!this.thread) return '';
        const { live } = this.thread;
        return [this.thread.messages.length, live ? live.draft.length : 0, live ? live.steps.length : 0].join(':');
      },
    },
    watch: {
      signature() {
        if (this.stick) this.$nextTick(this.scrollDown);
      },
      thread(value, old) {
        if (!old || !value || value.conversation !== old.conversation) {
          this.exportError = '';
          this.stick = true;
          this.$nextTick(this.scrollDown);
        }
      },
    },
    mounted() {
      this.scrollDown();
    },
    methods: {
      isLive(message) {
        return !!(this.thread.live && this.thread.live.messageId === message.id);
      },
      meta(message) {
        return message.meta || {};
      },
      stats(message) {
        return this.meta(message).stats || {};
      },
      stepsOf(message) {
        return this.isLive(message) ? this.thread.live.steps : this.meta(message).steps || [];
      },
      bodyOf(message) {
        return this.isLive(message) ? this.thread.live.draft : message.content;
      },
      notesOf(message) {
        return this.isLive(message) ? [] : (this.meta(message).notes || []).filter(Boolean);
      },
      refsOf(message) {
        const refs = this.meta(message).evidenceRefs;
        return refs && refs.length ? refs : citedIds(message.content);
      },
      evidenceOf(message) {
        return this.stepsOf(message).flatMap((step) => step.evidence || []);
      },
      evidenceLabel(message) {
        const refs = this.refsOf(message).length;
        return refs ? `Доказательства · ${refs}` : 'Собранные материалы';
      },
      numbers(message) {
        return this.refsOf(message).reduce((result, id, index) => ({ ...result, [id]: index + 1 }), {});
      },
      render(message) {
        const text = this.bodyOf(message);
        return renderAnswer(text, this.isLive(message) ? {} : this.numbers(message));
      },
      plain(text) {
        return renderPlain(text);
      },
      // Files attached to a question: [{ id, name, size, type, evidence }].
      filesOf(message) {
        const files = this.meta(message).attachments;
        return Array.isArray(files) ? files.filter(Boolean) : [];
      },
      // The card of the file opens in the panel of the answer that follows the question, after its cited
      // evidence when the answer does not cite the file itself.
      openAttachment(message, file) {
        if (!file.evidence) return;
        const { messages } = this.thread;
        const next = messages[messages.indexOf(message) + 1];
        const answer = next && next.role === 'assistant' ? next : null;
        this.$emit('cite', {
          eid: file.evidence,
          messageId: answer ? answer.id : message.id,
          ids: answer ? this.refsOf(answer) : [],
          related: true,
        });
      },
      problem(message) {
        if (this.isLive(message)) return null;
        const meta = this.meta(message);
        const known = PROBLEMS[meta.status];
        if (meta.status === 'failed') return { ...known, text: meta.error || known.text };
        if (known) return known;
        if (meta.partial && meta.reason === 'length') {
          return { tone: 'warning', text: 'Ответ сокращён: достигнут лимит длины ответа.' };
        }
        return null;
      },
      statsText(message) {
        const stats = this.stats(message);
        if (!stats.durationMs) return '';
        return duration(stats.durationMs);
      },
      statsHint(message) {
        const stats = this.stats(message);
        const lines = [];
        if (stats.model) lines.push(`Модель: ${stats.model}`);
        if (stats.llmCalls) lines.push(`Обращений к модели: ${stats.llmCalls}`);
        if (stats.toolCalls) lines.push(`Запросов к данным: ${stats.toolCalls}`);
        if (stats.completionTokens) lines.push(`Сгенерировано токенов: ${tokens(stats.completionTokens)}`);
        if (stats.tokensPerSecond) lines.push(`Скорость: ${Math.round(stats.tokensPerSecond)} ток/с`);
        if (stats.compactions) lines.push(`Сжатий контекста: ${stats.compactions}`);
        return lines.join('\n');
      },
      async copy(message) {
        try {
          await navigator.clipboard.writeText(message.content);
          this.copied = message.id;
          setTimeout(() => {
            if (this.copied === message.id) this.copied = null;
          }, 1500);
        } catch (error) {
          this.copied = null;
        }
      },
      // The PDF is built in the browser (export-pdf.js); the module and its libraries load on the first export.
      async exportPdf() {
        if (!this.canExport) return;
        const { conversation, messages } = this.thread;
        this.exporting = true;
        this.exportError = '';
        try {
          const { exportConversationPdf } = await import(/* webpackChunkName: "ai-pdf" */ './export-pdf');
          await exportConversationPdf({
            conversation,
            messages,
            numbers: messages
              .filter((message) => message.role === 'assistant')
              .reduce((result, message) => ({ ...result, [message.id]: this.numbers(message) }), {}),
            cached: this.$store.getters['assistant/evidenceFor'](conversation.id),
            fetchEvidence: (ids) => getEvidence(conversation.id, ids).then(({ data }) => data.items || []),
            router: this.$router,
            sources: (this.$store.state.assistant.capabilities || {}).sources || [],
          });
        } catch (error) {
          this.exportError = 'Не удалось сформировать PDF. Попробуйте ещё раз.';
        } finally {
          this.exporting = false;
        }
      },
      onClick(event) {
        const chip = event.target.closest && event.target.closest('.ai-cite');
        if (!chip) return;
        const container = chip.closest('.ai-message');
        const index = [...this.$el.querySelectorAll('.ai-message')].indexOf(container);
        const message = this.thread.messages[index];
        this.$emit('cite', {
          eid: chip.dataset.eid,
          messageId: message && message.id,
          ids: message ? this.refsOf(message) : [],
        });
      },
      onScroll() {
        const el = this.$refs.scroller;
        this.stick = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
      },
      scrollDown() {
        const el = this.$refs.scroller;
        if (el) el.scrollTop = el.scrollHeight;
      },
    },
  };
</script>

<style lang="scss" scoped>
  .ai-thread {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 14px;
    min-height: 0;
    padding: 16px 18px 20px;
    overflow-y: auto;
  }

  .ai-welcome {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin: auto;
    padding: 24px 12px;
    max-width: 460px;
    text-align: center;
  }

  .ai-welcome__badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background: var(--ui-primary-soft);
    color: var(--ui-primary);
  }

  .ai-welcome__title {
    margin: 12px 0 6px;
    color: var(--ui-text);
    font-size: 17px;
    font-weight: 500;
  }

  .ai-welcome__text {
    margin: 0 0 16px;
    color: var(--ui-text-muted);
    font-size: 13px;
    line-height: 1.5;
  }

  .ai-welcome__examples {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 8px;
  }

  .ai-thread__loading,
  .ai-thread__error {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0;
    color: var(--ui-text-muted);
    font-size: 13px;
  }

  .ai-thread__error {
    color: var(--ui-danger);
  }

  .ai-scope {
    padding: 10px 12px;
    border: 1px solid var(--ui-border);
    border-radius: var(--ui-radius);
    background: var(--ui-surface-soft);
  }

  .ai-scope__head {
    display: flex;
    align-items: flex-start;
    gap: 10px;
  }

  .ai-scope__title {
    display: flex;
    flex: 1 1 auto;
    align-items: center;
    gap: 7px;
    min-width: 0;
    min-height: 22px;
    color: var(--ui-text);
    font-weight: 500;
    overflow-wrap: anywhere;
  }

  .ai-scope__export {
    flex: 0 0 auto;
    margin: -2px -2px -2px 0;
    white-space: nowrap;
  }

  .ai-scope__error {
    margin: 6px 0 0;
    color: var(--ui-danger);
    font-size: 12px;
  }

  // A chat has no scope: a light title row above the messages.
  .ai-scope.is-chat {
    padding: 0 0 10px;
    border: 0;
    border-bottom: 1px solid var(--ui-border);
    border-radius: 0;
    background: none;

    .ai-scope__title {
      color: var(--ui-text-muted);

      span {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }
  }

  .ai-scope__facts {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 14px;
    margin-top: 4px;
    color: var(--ui-text-muted);
    font-size: 12px;
  }

  .ai-message {
    display: flex;
    flex-direction: column;
    min-width: 0;

    &.is-user {
      align-items: flex-end;
    }
  }

  .ai-bubble {
    max-width: 82%;
    padding: 9px 13px;
    border-radius: var(--ui-radius-lg) var(--ui-radius-lg) 4px var(--ui-radius-lg);
    background: var(--ui-primary-soft);
    color: var(--ui-text);
    overflow-wrap: anywhere;
  }

  // Files of a question, under its text (or alone), aligned with the bubble.
  .ai-message__files {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 6px;
    max-width: 82%;
    margin: 0;
    padding: 0;
    list-style: none;

    li {
      display: flex;
      min-width: 0;
      max-width: 100%;
    }
  }

  .ai-bubble + .ai-message__files {
    margin-top: 6px;
  }

  .ai-typing {
    display: inline-flex;
    gap: 4px;
    padding: 6px 2px;

    span {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: var(--ui-border-strong);
      animation: ai-typing 1.2s infinite ease-in-out;
    }

    span:nth-child(2) {
      animation-delay: 0.15s;
    }

    span:nth-child(3) {
      animation-delay: 0.3s;
    }
  }

  @keyframes ai-typing {
    0%,
    80%,
    100% {
      opacity: 0.35;
      transform: translateY(0);
    }

    40% {
      opacity: 1;
      transform: translateY(-3px);
    }
  }

  .ai-notes {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin: 8px 0 0;
    padding: 8px 10px;
    border-radius: var(--ui-radius-sm);
    background: var(--ui-surface-soft);
    color: var(--ui-text-muted);
    font-size: 12px;
    list-style: none;

    li {
      display: flex;
      gap: 6px;
    }
  }

  .ai-problem {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    margin-top: 8px;
    padding: 8px 10px;
    border-radius: var(--ui-radius-sm);
    font-size: 13px;

    span {
      flex: 1 1 200px;
    }
  }

  .ai-message__footer {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px 8px;
    margin-top: 10px;
  }

  .ai-message__stats {
    margin-left: auto;
    color: var(--ui-text-muted);
    font-size: 12px;
  }
</style>
